import { FC, useMemo } from 'react';
import { Button, Descriptions, Divider } from 'antd';
import styled from 'styled-components';
import * as Icon from 'react-feather';
import {
  DataSubjectCommentDto,
  DataSubjectDto,
  formatDate,
  formatDateTime,
  Padding,
  sortDate,
  Stack,
  emitCreateFormTableRow,
  TextSecondary,
  getDayjsNow,
  getDayjs,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';

import translations from './DataSubjectDescription.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-description'
);
addTranslations(translations);

type Props = {
  data: DataSubjectDto;
  isEditable?: boolean;
};

export const DataSubjectDescription: FC<Props> = ({
  data,
  isEditable = false,
}) => {
  const { t } = useTranslation();

  const lastComment = useMemo(
    () =>
      data.comments.reduce((prev, curr) => {
        if (!prev) return curr;
        return sortDate(prev.createdAt, curr.createdAt, true) === 1
          ? prev
          : curr;
      }, undefined as DataSubjectCommentDto | undefined),
    [data.comments]
  );

  return (
    <>
      <Padding horizontal top>
        <Descriptions
          column={1}
          items={[
            {
              key: 'businessArea',
              label: t('field.businessArea.label'),
              children: data.businessArea
                ? [data.businessArea.name, data.businessArea.code].join(', ')
                : '',
            },
            {
              key: 'economicSector',
              label: t('field.economicSector.label'),
              children: data.economicSector
                ? [data.economicSector.name, data.economicSector.code].join(
                    ', '
                  )
                : '',
            },
            {
              key: 'legalForm',
              label: t('field.legalForm.label'),
              children: data.legalForm?.name || '',
            },
            {
              key: 'validFrom',
              label: t('field.validFrom.label'),
              children: data.validFrom ? formatDate(data.validFrom) : '',
            },
            {
              key: 'validTo',
              label: t('field.validTo.label'),
              children: data.validTo ? formatDate(data.validTo) : '',
            },
            {
              key: 'isActive',
              label: t('field.isActive.label'),
              children:
                (!data.validFrom ||
                  getDayjs(data.validFrom)?.isBefore(
                    getDayjsNow().add(1, 'day'),
                    'day'
                  )) &&
                (!data.validTo ||
                  getDayjs(data.validTo)?.isAfter(
                    getDayjsNow().subtract(1, 'day'),
                    'day'
                  ))
                  ? t('field.isActive.yes')
                  : t('field.isActive.no'),
            },
            {
              key: 'isFictitious',
              label: t('field.isFictitious.label'),
              children:
                typeof data.isFictitious !== 'boolean'
                  ? ''
                  : data.isFictitious
                  ? t('field.isFictitious.yes')
                  : t('field.isFictitious.no'),
            },
            {
              key: 'isPublic',
              label: t('field.isPublic.label'),
              children:
                typeof data.isPublic !== 'boolean'
                  ? ''
                  : data.isPublic
                  ? t('field.isPublic.yes')
                  : t('field.isPublic.no'),
            },
          ]}
        />
      </Padding>
      <Divider />
      <Padding horizontal bottom>
        <Stack size="md">
          <TextSecondary>{t('lastComment.title')}</TextSecondary>
          {lastComment?.comments}
          <CommentFooter>
            {lastComment ? (
              <TextSecondary>{getTimestamp(lastComment)}</TextSecondary>
            ) : (
              t('lastComment.noComments')
            )}
            {isEditable && (
              <Button
                id="create-data-subject-comment"
                onClick={() => emitCreateFormTableRow('comments')}
              >
                <Stack size="xs" direction="horizontal" align="center">
                  <Icon.Plus size={16} />
                  {t('lastComment.btn')}
                </Stack>
              </Button>
            )}
          </CommentFooter>
        </Stack>
      </Padding>
    </>
  );
};

const getTimestamp = (comment: DataSubjectCommentDto) =>
  comment.modifiedAt
    ? `${formatDateTime(comment.modifiedAt)} (${comment.modifiedBy?.username})`
    : `${formatDateTime(comment.createdAt)} (${comment.createdBy?.username})`;

const CommentFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
